import React from 'react';
import { ITemplateVariable, IArgumentMetadata } from '@tymely/atoms';
import { Box, styled, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Loader } from '@tymely/components';
import { LocalizationProvider } from '@mui/x-date-pickers';

import Variable from './Variable';
import { useTemplateEditor } from './TemplateEditorProvider';

const _Root = styled(Box)`
    flex: 1;
    padding-top: ${({ theme }) => theme.spacing(1)};
    overflow: auto;
`;

const VariableList = () => {
    const { setVariables, argsVariables, argumentsMetadataQuery, freeVariablesQuery, argumentsQuery } =
        useTemplateEditor();

    const argMetadata = React.useMemo(
        () =>
            argumentsMetadataQuery.data?.reduce<Record<string, IArgumentMetadata>>(
                (acc, item) => ({ ...acc, [item.name]: item }),
                {},
            ),
        [argumentsMetadataQuery.data],
    );

    const _onVariableChange = (varName: string, variable: ITemplateVariable) => {
        const vars = {
            ...argsVariables,
            [varName]: { ...argsVariables[varName], ...variable },
        };
        setVariables(vars);
    };

    if (Object.keys(argsVariables).length === 0) {
        if (freeVariablesQuery.isError || argumentsMetadataQuery.isError) {
            return <Typography color="error">Error</Typography>;
        }

        if (freeVariablesQuery.isLoading || argumentsMetadataQuery.isLoading || argumentsQuery?.isFetching) {
            return <Typography>Loading...</Typography>;
        }

        return <Typography color="gray">No free variables detected in the template</Typography>;
    }

    if (!argMetadata) {
        return <Loader />;
    }

    return (
        <_Root>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                {Object.keys(argsVariables)
                    .sort((v1, v2) => v1.localeCompare(v2))
                    .map((variableName) => (
                        <Variable
                            key={variableName}
                            name={variableName}
                            value={argsVariables[variableName]}
                            argMetadata={argMetadata[variableName]}
                            onChange={_onVariableChange}
                        />
                    ))}
            </LocalizationProvider>
        </_Root>
    );
};

export default VariableList;
